"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useReducedMotion } from "@/lib/useReducedMotion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { photos } from "@/lib/images";

const slides = [photos.heroMain, photos.gallery[1], photos.gallery[2], photos.gallery[4]];

/** Header height (h-[68px]) - the hero fills everything below it, so the fold is exactly one image. */
const HEADER_H = "68px";

/** Full-viewport hero: rotating photos edge to edge with a short headline, one line and two buttons. */
export function H9Hero() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const go = useCallback((n: number) => setI((n + slides.length) % slides.length), []);

  useEffect(() => {
    if (paused || reduce) return;
    const t = setInterval(() => setI((v) => (v + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [paused, reduce]);

  return (
    <>
      <section
        className="relative w-full overflow-hidden bg-stone-200"
        style={{ height: `calc(100svh - ${HEADER_H})`, minHeight: "30rem" }}
        role="region"
        aria-roledescription="carousel"
        aria-label="My Value Academy"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div key={i} className={`absolute inset-0 brightness-[0.7] ${i === 0 ? "" : "hero-in"}`}>
          <Image src={slides[i]} alt="" fill priority={i === 0} sizes="100vw" className="object-cover" />
        </div>

        <div className="relative z-10 flex h-full items-center">
          <div className="w-full px-[clamp(1.25rem,6vw,7rem)]">
            <h1
              style={{ animationDelay: "0.06s" }}
              className="hero-text-in max-w-4xl font-display text-[2.6rem] font-medium leading-[1.04] !text-white sm:text-6xl lg:text-7xl"
            >
              Learn a skill that moves your <em className="font-normal text-inherit">career</em> forward.
            </h1>
            <p
              style={{ animationDelay: "0.14s" }}
              className="hero-text-in mt-5 max-w-xl text-lg text-white/90"
            >
              Live, mentor-led programs built around real projects.
            </p>
            <div
              style={{ animationDelay: "0.22s" }}
              className="hero-text-in mt-8 flex flex-wrap gap-3"
            >
              <Link href="/courses" className="he-btn gold">Explore courses <ArrowRight className="h-4 w-4" /></Link>
              <Link href="/contact?tab=enquiry" className="he-btn wire">Talk to an advisor</Link>
            </div>
          </div>
        </div>

        <button type="button" aria-label="Previous image" onClick={() => go(i - 1)} className="absolute left-3 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white transition hover:bg-brand-600 sm:flex lg:left-6">
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button type="button" aria-label="Next image" onClick={() => go(i + 1)} className="absolute right-3 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white transition hover:bg-brand-600 sm:flex lg:right-6">
          <ChevronRight className="h-6 w-6" />
        </button>

        <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
          {slides.map((_, n) => (
            <button key={n} type="button" aria-label={`Go to image ${n + 1}`} aria-current={n === i} onClick={() => go(n)} className="flex h-6 items-center justify-center px-2"><span className={`block h-2 rounded-full transition-all ${n === i ? "w-8 bg-gold-400" : "w-2 bg-white/60"}`} /></button>
          ))}
        </div>
      </section>
    </>
  );
}
